import React, { useState, useRef, useEffect } from "react";
import { gsap } from "gsap";
import "./navbar.scss";
import { Link, useLocation } from "react-router-dom";
import img from "../../assets/logo/new-logo.png";
import imgw from "../../assets/logo/new-logo-white.png";
import { MdKeyboardArrowDown } from "react-icons/md";
import { Twirl as Hamburger } from "hamburger-react";
import { IoCallOutline } from "react-icons/io5";
import { getCountryPhoneDetails } from "../../utils/countryUtils";

const Navbar = () => {
  const [isOpen, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  const [phoneDetails, setPhoneDetails] = useState({ number: '', flag: '' });

  // Refs for GSAP animation
  const logoRef = useRef(null);
  const linksRef = useRef([]);
  const ctaRef = useRef(null);

  // Navbar links and submenu
  const navbarLinks = [
    { name: "About", path: "/about" },
    { name: "Blogs", path: "/blogs" },
    { name: "Patients", path: "/services" },
    { name: "Testimonials", path: "/#testimonials" },
    { name: "Clinic Locations", path: "/axoncareClinic" },
    { name: "AxonCare Network",
      submenu: [
        { name: "Doctors", path: "/axoncareNetwork" },
        { name: "Hospitals and Clinics", path: "/axoncareNetwork#hospitalsandclinics" },
        { name: "Labs & Pharmacies", path: "/axoncareNetwork#labsandpharma" },
      ],
    },
  ];

  const ctaText = `${phoneDetails.number} ${phoneDetails.flag}`;

  // GSAP Animation on page load
  useEffect(() => {
    const fetchCountryDetails = async () => {
      const details = await getCountryPhoneDetails();
      setPhoneDetails(details);
    };
    
    fetchCountryDetails();
    
    gsap.from(logoRef.current, {
      x: -200,
      opacity: 0,
      duration: 1,
      ease: "power3.out",
    });

    gsap.from([...linksRef.current, ctaRef.current], {
      opacity: 0,
      y: -50,
      duration: 0.8,
      ease: "power3.out",
      stagger: 0.2,
    });
  }, []);

  const location = useLocation();

  // Handle scroll state
  useEffect(() => {
    if (location.pathname !== "/service") {
      const handleScroll = () => {
        setScrolled(window.scrollY > 60);
      };
      window.addEventListener("scroll", handleScroll);
      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    }
  }, [location.pathname]);

  // Handle testimonials link click
  const handleTestimonialsClick = (e) => {
    e.preventDefault();
    
    // If we're not on the home page, navigate to home first
    if (location.pathname !== '/') {
      window.location.href = '/#testimonials';
      return;
    }
    
    // If we're on the home page, scroll to testimonials section
    const testimonialsSection = document.getElementById('testimonials');
    if (testimonialsSection) {
      testimonialsSection.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  // Handle labs and pharmacies link click
  const handleLabsPharmaciesClick = (e) => {
    e.preventDefault();
    
    // If we're not on the axoncare network page, navigate there first
    if (location.pathname !== '/axoncareNetwork') {
      window.location.href = '/axoncareNetwork#labsandpharma';
      return;
    }
    
    // If we're on the axoncare network page, scroll to labs and pharmacies section
    const labsPharmaciesSection = document.getElementById('labsandpharma');
    if (labsPharmaciesSection) {
      labsPharmaciesSection.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  // Handle hospitals and clinics link click
  const handleHospitalsClinicsClick = (e) => {
    e.preventDefault();
    
    // If we're not on the axoncare network page, navigate there first
    if (location.pathname !== '/axoncareNetwork') {
      window.location.href = '/axoncareNetwork#hospitalsandclinics';
      return;
    }
    
    // If we're on the axoncare network page, scroll to hospitals and clinics section
    const hospitalsClinicsSection = document.getElementById('hospitalsandclinics');
    if (hospitalsClinicsSection) {
      hospitalsClinicsSection.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  return (
    <div className={scrolled ? "navbar-parent parent" : "navbar-parent parent scrolled-navbar"}>
      <div className="navbar-container container">
        {/* Logo */}
        <div className="logo-container">
          <Link to="/" className="logo-img">
            <img src={img} alt="Logo" ref={logoRef} />
          </Link>
          <a 
            href="https://axonichealth.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="axonic-health-link"
          >
            by axonic health
          </a>
        </div>


        {/* Dynamic Desktop Links with Submenu */}
        <div className="links-box">
          {navbarLinks.map((link, index) => (
            link.submenu ? (
              <div className="submenu" key={index}>
                <div className="submenu-heading">
                  <Link to={link.path || "#"} ref={(el) => (linksRef.current[index] = el)}> {link.name} <span className="rotate-span"><MdKeyboardArrowDown /></span></Link>
                  <div className="submenu-links">
                    {link.submenu.map((subLink, subIndex) => (
                      subLink.name === "Labs & Pharmacies" ? (
                        <a
                          key={subIndex}
                          href="/axoncareNetwork#labsandpharma"
                          onClick={handleLabsPharmaciesClick}
                        >
                          {subLink.name}
                        </a>
                      ) : subLink.name === "Hospitals and Clinics" ? (
                        <a
                          key={subIndex}
                          href="/axoncareNetwork#hospitalsandclinics"
                          onClick={handleHospitalsClinicsClick}
                        >
                          {subLink.name}
                        </a>
                      ) : (
                        <Link key={subIndex} to={subLink.path}>
                          {subLink.name}
                        </Link>
                      )
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              link.name === "Testimonials" ? (
                <a
                  key={index}
                  href="/#testimonials"
                  onClick={handleTestimonialsClick}
                  className={scrolled ? "scrolled-hover" : ""}
                  ref={(el) => (linksRef.current[index] = el)}
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={index}
                  to={link.path}
                  className={scrolled ? "scrolled-hover" : ""}
                  ref={(el) => (linksRef.current[index] = el)}
                >
                  {link.name}
                </Link>
              )
            )
          ))}
        </div>

        {/* Hamburger for Mobile */}
        <div className="hamburger-box">
          <Hamburger toggled={isOpen} toggle={setOpen} color={isOpen ? "#ffffff" : "#000000"} />
        </div>

        {/* Dynamic CTA Button */}
        {ctaText && (
          <div className="cta-box">
            <a href="tel:+18002101999" className="cta-btn" ref={ctaRef}>
              <div className="header-icon"><IoCallOutline /></div>{ctaText}
            </a>
          </div>
        )}


      </div>

      {/* Dynamic Mobile Menu */}
      {isOpen && (
        <div className="mobile-navbar-parent">
          <div className="mobile-navbar-container">
            <Link to="/" className="mobile-logo-img" onClick={() => setOpen(false)}>
              <img src={imgw} alt="Logo" className="mobile-logo" />
            </Link>

            {/* Dynamic Mobile Links */}
            <div className="mobile-menu-links">
              {navbarLinks.map((link, index) => (
                link.submenu ? (
                  <div className="phone-menu" key={index}>
                    <div className="phone-menu-heading" >
                      <Link to={link.path || "#"} onClick={() => setOpen(false)}>{link.name} </Link><span className="handle-hover"  onClick={() => setMenuOpen(!menuOpen)}><MdKeyboardArrowDown /></span>
                    </div>

                    {/* Toggleable Submenu */}
                    <div className={menuOpen ? "phone-menu-links" : "phone-menu-links-active"}>
                      {link.submenu.map((subLink, subIndex) => (
                        subLink.name === "Labs & Pharmacies" ? (
                          <a
                            key={subIndex}
                            href="/axoncareNetwork#labsandpharma"
                            onClick={(e) => {
                              handleLabsPharmaciesClick(e);
                              setOpen(false);
                            }}
                          >
                            {subLink.name}
                          </a>
                        ) : subLink.name === "Hospitals and Clinics" ? (
                          <a
                            key={subIndex}
                            href="/axoncareNetwork#hospitalsandclinics"
                            onClick={(e) => {
                              handleHospitalsClinicsClick(e);
                              setOpen(false);
                            }}
                          >
                            {subLink.name}
                          </a>
                        ) : (
                          <Link key={subIndex} to={subLink.path} onClick={() => setOpen(false)} >
                            {subLink.name}
                          </Link>
                        )
                      ))}
                    </div>
                  </div>
                ) : (
                  link.name === "Testimonials" ? (
                    <a 
                      key={index} 
                      href="/#testimonials" 
                      onClick={(e) => {
                        handleTestimonialsClick(e);
                        setOpen(false);
                      }}
                    >
                      {link.name}
                    </a>
                  ) : (
                    <Link key={index} to={link.path} onClick={() => setOpen(false)}>
                      {link.name}
                    </Link>
                  )
                )
              ))}
            </div>

            {/* Dynamic Mobile CTA Button */}
            {ctaText && (
              <a href="tel:+18002101999" className="mobile-cta-box">
                <button className="cta-btn">
                  <div className="header-icon"><IoCallOutline /></div>{ctaText}
                </button>
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Navbar;
